import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../stores/authStore';
import { useToast } from '../../components/Toast';
import type { Competitor, TrackedPrompt } from '../../types/database';

type OnboardingContext = {
  canExitOnboarding?: boolean;
  previousWorkspaceId?: string | null;
  createdWorkspaceId?: string | null;
};

export function OnboardingStep3() {
  const navigate = useNavigate();
  const { workspace, user, profile, fetchProfile } = useAuthStore();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [newPromptText, setNewPromptText] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [isGenerating, setIsGenerating] = useState(true);
  const [isExiting, setIsExiting] = useState(false);
  const [removingPromptIds, setRemovingPromptIds] = useState<string[]>([]);
  const [onboardingContext, setOnboardingContext] = useState<OnboardingContext | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const generateInFlight = useRef(false);

  useEffect(() => {
    const rawContext = sessionStorage.getItem('lima_onboarding_context');
    if (!rawContext) {
      setOnboardingContext(null);
      return;
    }

    try {
      setOnboardingContext(JSON.parse(rawContext) as OnboardingContext);
    } catch {
      setOnboardingContext(null);
    }
  }, []);

  const { data: prompts = [], isLoading } = useQuery({
    queryKey: ['tracked_prompts', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return [];
      const { data, error } = await supabase
        .from('tracked_prompts')
        .select('*')
        .eq('workspace_id', workspace.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as TrackedPrompt[];
    },
    enabled: !!workspace?.id,
  });

  // Step 2 needs at least one competitor before step 3 is reachable
  const { data: competitors = [], isLoading: competitorsLoading } = useQuery({
    queryKey: ['competitors', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return [];
      const { data, error } = await supabase
        .from('competitors')
        .select('*')
        .eq('workspace_id', workspace.id)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data as Competitor[];
    },
    enabled: !!workspace?.id,
  });

  const promptLimit = workspace?.prompt_limit;
  const hasReachedPromptLimit = promptLimit !== undefined && prompts.length >= promptLimit;
  // Nothing is collected with no prompts, so the last one can't be removed (#3).
  const isLastPrompt = prompts.length === 1;

  useEffect(() => {
    if (!workspace?.brand_url) {
      navigate('/onboarding/step-1', { replace: true });
      return;
    }

    if (competitorsLoading) return;
    if (competitors.length === 0) {
      navigate('/onboarding/step-2', { replace: true });
      return;
    }

    if (prompts.length > 0) {
      setIsGenerating(false);
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }

    if (isGenerating && !pollRef.current) {
      if (!generateInFlight.current) {
        generatePromptsFallback();
      }

      pollRef.current = setInterval(() => {
        queryClient.invalidateQueries({ queryKey: ['tracked_prompts', workspace?.id] });
      }, 3000);
    }

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [workspace?.brand_url, competitorsLoading, competitors.length, prompts.length, isGenerating]);

  const generatePromptsFallback = async () => {
    if (!workspace?.id || !workspace?.brand_url || generateInFlight.current) return;
    generateInFlight.current = true;

    try {
      const { error } = await supabase.functions.invoke('generate-prompts', {
        body: {
          workspace_id: workspace.id,
          brand_url: workspace.brand_url,
        },
      });

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ['tracked_prompts', workspace.id] });
    } catch (error: any) {
      console.error('Error generating prompts (fallback):', error);
      toast.error('Could not auto-generate prompts. Add them manually below.');
    } finally {
      setIsGenerating(false);
      generateInFlight.current = false;
    }
  };

  const handleAddPrompt = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPromptText.trim() || !workspace?.id || hasReachedPromptLimit) return;

    setIsAdding(true);
    try {
      const trimmed = newPromptText.trim();

      const exists = prompts.some(p => p.text.toLowerCase() === trimmed.toLowerCase());
      if (exists) {
        toast.error('This prompt already exists');
        setIsAdding(false);
        return;
      }

      const { error } = await supabase.from('tracked_prompts').insert({
        workspace_id: workspace.id,
        text: trimmed,
      });

      if (error) throw error;

      setNewPromptText('');
      queryClient.invalidateQueries({ queryKey: ['tracked_prompts', workspace.id] });
    } catch (error: any) {
      console.error('Error adding prompt:', error);
      toast.error(error.message || 'Failed to add prompt');
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemovePrompt = async (id: string) => {
    if (isLastPrompt) {
      toast.info("You need at least one prompt. Lima stops tracking your brand without one, so add another prompt before removing this one.");
      return;
    }
    if (!workspace?.id || removingPromptIds.includes(id)) return;

    const previousPrompts = prompts;
    setRemovingPromptIds((current) => [...current, id]);

    queryClient.setQueryData<TrackedPrompt[]>(
      ['tracked_prompts', workspace.id],
      previousPrompts.filter((prompt) => prompt.id !== id)
    );

    try {
      const { error } = await supabase.from('tracked_prompts').delete().eq('id', id);
      if (error) throw error;

      await queryClient.invalidateQueries({ queryKey: ['tracked_prompts', workspace.id] });
    } catch (error: any) {
      queryClient.setQueryData(['tracked_prompts', workspace.id], previousPrompts);
      toast.error('Failed to remove prompt');
    } finally {
      setRemovingPromptIds((current) => current.filter((promptId) => promptId !== id));
    }
  };

  const handleFinish = () => {
    navigate('/onboarding/step-4');
  };

  const handleGoBack = () => {
    navigate('/onboarding/step-2');
  };

  const handleCancel = async () => {
    if (!user || !profile?.workspace_id) return;

    setIsExiting(true);
    try {
      const targetWorkspaceId = onboardingContext?.previousWorkspaceId || profile.workspace_id;
      const createdWorkspaceId = onboardingContext?.createdWorkspaceId;

      const { error } = await supabase
        .from('profiles')
        .update({
          workspace_id: targetWorkspaceId,
          onboarding_completed: true,
        })
        .eq('id', user.id);

      if (error) throw error;

      if (
        createdWorkspaceId &&
        createdWorkspaceId !== targetWorkspaceId &&
        profile.workspace_id === createdWorkspaceId
      ) {
        await supabase.from('workspace_members').delete().eq('workspace_id', createdWorkspaceId);
        await supabase.from('workspaces').delete().eq('id', createdWorkspaceId);
      }

      sessionStorage.removeItem('lima_onboarding_context');
      await fetchProfile(user.id);
      queryClient.invalidateQueries({ queryKey: ['userWorkspaces'] });
      navigate('/overview', { replace: true });
    } catch (error: any) {
      toast.error(error?.message || 'Failed to exit onboarding');
    } finally {
      setIsExiting(false);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="app-wrapper">
        <div id="no-saved-ads" className="onboarding-wrapper">
          <div className="intro-wrapper">
            {onboardingContext?.canExitOnboarding && (
              <>
                <button type="button" className="link-style" onClick={handleCancel} disabled={isExiting}>
                  Cancel
                </button>
                <div className="spacer-small"></div>
              </>
            )}
            <div className="text-size-large text-weight-semibold text-colour-dark-brown">
              {isGenerating && prompts.length === 0
                ? "We're writing some prompts for you"
                : "Here are some prompts to get you started"}
            </div>
            <div className="spacer-xxtiny"></div>
            <div className="text-size-regular text-colour-alt-grey align-center max-width-small">
              {isGenerating && prompts.length === 0
                ? "This may take a few seconds..."
                : "We'll use these prompts to compare how you and your competitors rank in AI search."}
            </div>
            <div className="plus-btn"></div>
            <div className="spacer-small"></div>

            <div className="card is-onboarding is-prompt-box">
              <div className="box-header-row">
                <div className="text-size-regular text-weight-semibold">
                  Prompt list{' '}
                  {prompts.length > 0 && (
                    <span className="number-pill">
                      {promptLimit ? `${prompts.length}/${promptLimit}` : prompts.length}
                    </span>
                  )}
                </div>
              </div>

              <div className="list-wrapper">
                {(isGenerating || isLoading) && prompts.length === 0 ? (
                  <div className="step-loader is-shimmer">
                    <div className="search-icon-embed">
                      <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 7v14" />
                        <path d="M16 12h2" />
                        <path d="M16 8h2" />
                        <path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z" />
                        <path d="M6 12h2" />
                        <path d="M6 8h2" />
                      </svg>
                    </div>
                    <div className="spacer-tiny"></div>
                    <div className="text-size-regular text-weight-medium text-colour-light-grey">
                      Writing prompts...
                    </div>
                  </div>
                ) : (
                  prompts.map((prompt, index) => (
                    <div
                      key={prompt.id}
                      className={`competitor-row${index === 0 ? ' is-top' : ''}`}
                    >
                      <div className="text-size-small text-weight-medium">{prompt.text}</div>
                      <div
                        className={`remove-icon-holder${removingPromptIds.includes(prompt.id) ? ' is-disabled' : ''}`}
                        style={isLastPrompt ? { opacity: 0.2, cursor: 'not-allowed' } : undefined}
                        onClick={() => handleRemovePrompt(prompt.id)}
                        title={isLastPrompt ? 'Add another prompt before removing this one. Tracking stops with no prompts.' : undefined}
                      >
                        <div className="embed-icon">
                          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M18 6 6 18" />
                            <path d="m6 6 12 12" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="competitor-form">
                <form onSubmit={handleAddPrompt} className="url-form">
                  <label htmlFor="prompt-text" className="form_label has-left-spacing">
                    Add prompt{' '}
                    <span className="text-colour-alt-grey">
                      {hasReachedPromptLimit
                        ? `(your plan allows ${promptLimit} prompts - remove one to add another)`
                        : prompts.length === 0 && !isGenerating
                          ? '(add at least one to start tracking)'
                          : '(optional - can do later)'}
                    </span>
                  </label>
                  <input
                    className="form_input is-url-input is-white has-inline-add-button w-input"
                    maxLength={256}
                    placeholder="e.g. what's the best software to help me run my small hotel?"
                    type="text"
                    id="prompt-text"
                    value={newPromptText}
                    onChange={(e) => setNewPromptText(e.target.value)}
                    disabled={isAdding || hasReachedPromptLimit}
                  />
                  <button
                    type="submit"
                    className={`button is-small is-form-button w-button${newPromptText.trim() && !hasReachedPromptLimit ? '' : ' is-lighter'}`}
                    disabled={isAdding || !newPromptText.trim() || hasReachedPromptLimit}
                  >
                    Add
                  </button>
                </form>
              </div>
            </div>

            <div className="spacer-small"></div>
            <div className="align-content-middle">
              <button
                onClick={handleFinish}
                className="button w-button"
                disabled={isGenerating || prompts.length === 0 || removingPromptIds.length > 0}
              >
                Finish onboarding
              </button>
              <div className="spacer-xxsmall"></div>
              <button onClick={handleGoBack} className="link-style">
                or go back
              </button>
            </div>
          </div>
        </div>
        <div className="gradient-style"></div>
      </div>
    </div>
  );
}
